import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { settingsService } from '../../services/dataService';
import styles from './SettingsErrorLogsPage.module.css';

const PAGE_SIZE = 50;
const toIso = (value) => (value ? new Date(value).toISOString() : undefined);

const SettingsLoginHistoryPage = () => {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [user, setUser] = useState('');
  const [callingUrl, setCallingUrl] = useState('');
  const [result, setResult] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [filters, setFilters] = useState({ user: '', callingUrl: '', result: '', startTime: '', endTime: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await settingsService.getLoginHistory({
        user: filters.user || undefined,
        calling_url: filters.callingUrl || undefined,
        result: filters.result || undefined,
        start_time: toIso(filters.startTime),
        end_time: toIso(filters.endTime),
        page,
        page_size: PAGE_SIZE,
      });
      setEntries(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.detail || 'Failed to load login history');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const applyFilters = (event) => {
    event.preventDefault();
    if (startTime && endTime && new Date(endTime) < new Date(startTime)) {
      setError('End time must be on or after start time');
      return;
    }
    setPage(1);
    setFilters({ user: user.trim(), callingUrl: callingUrl.trim(), result, startTime, endTime });
  };

  const clearFilters = () => {
    setUser('');
    setCallingUrl('');
    setResult('');
    setStartTime('');
    setEndTime('');
    setPage(1);
    setFilters({ user: '', callingUrl: '', result: '', startTime: '', endTime: '' });
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1>Login History</h1>
          <p>Successful and failed authentication attempts.</p>
        </div>
        <Link className={styles.backLink} to="/settings">Back to Settings</Link>
      </header>

      <section className={styles.panel}>
        <form className={styles.filters} onSubmit={applyFilters}>
          <label>
            User
            <input value={user} onChange={(event) => setUser(event.target.value)} placeholder="Username" />
          </label>
          <label>
            Result
            <select value={result} onChange={(event) => setResult(event.target.value)}>
              <option value="">All results</option>
              <option value="success">Successful</option>
              <option value="failed">Failed</option>
            </select>
          </label>
          <label>
            Calling URL
            <input value={callingUrl} onChange={(event) => setCallingUrl(event.target.value)} placeholder="Origin or referring URL" />
          </label>
          <label>
            From
            <input type="datetime-local" value={startTime} onChange={(event) => setStartTime(event.target.value)} />
          </label>
          <label>
            To
            <input type="datetime-local" value={endTime} onChange={(event) => setEndTime(event.target.value)} />
          </label>
          <div className={styles.filterActions}>
            <button type="submit">Apply</button>
            <button type="button" className={styles.secondaryButton} onClick={clearFilters}>Clear</button>
          </div>
        </form>
      </section>

      {error ? <div className={styles.error}>{error}</div> : null}

      <section className={styles.panel}>
        <div className={styles.summary}>{loading ? 'Loading…' : `${total} login event${total === 1 ? '' : 's'}`}</div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr><th>Time</th><th>User</th><th>Result</th><th>Client IP</th><th>Calling URL</th><th>Client</th><th>User Agent</th><th>Details</th></tr>
            </thead>
            <tbody>
              {!loading && entries.length === 0 ? (
                <tr><td colSpan="8" className={styles.empty}>No login events match the filters.</td></tr>
              ) : entries.map((entry) => (
                <tr key={entry.id}>
                  <td className={styles.nowrap}>{new Date(entry.occurred_at).toLocaleString()}</td>
                  <td>{entry.user}</td>
                  <td>
                    <span className={entry.successful ? styles.successStatus : styles.status}>
                      {entry.successful ? 'Success' : 'Failed'}
                    </span>
                  </td>
                  <td className={styles.nowrap}>{entry.client_ip || '—'}</td>
                  <td className={styles.callingUrl} title={entry.calling_url || ''}>{entry.calling_url || '—'}</td>
                  <td>{entry.client_type}</td>
                  <td className={styles.userAgent} title={entry.user_agent || ''}>{entry.user_agent || '—'}</td>
                  <td>{entry.failure_reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={styles.pagination}>
          <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)}>Previous</button>
          <span>Page {page} of {pageCount}</span>
          <button type="button" disabled={page >= pageCount || loading} onClick={() => setPage((value) => value + 1)}>Next</button>
        </div>
      </section>
    </div>
  );
};

export default SettingsLoginHistoryPage;
